import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Plus,
  LogOut,
  Video,
  Phone,
  Calendar,
  Settings,
  Search,
  Bell,
  ChevronRight,
  UserPlus,
  Clock,
  Activity,
  Heart,
  Sun,
  Moon,
} from "lucide-react";
import { useApp } from "../../shared/context/AppContext";
import UserCard from "./UserCard";
import CreateRoomModal from "./CreateRoomModal";
import IncomingCallModal from "../call/IncomingCallModal";
import ProfilePage from "../profile/ProfilePage";
import FriendsPage from "../friends/FriendsPage";
import FriendRequestsModal from "../friends/FriendRequestsModal";

const Dashboard = () => {
  const {
    currentUser,
    onlineUsers,
    leaveApp,
    callUser,
    incomingCall,
    incomingInvite,
    acceptCall,
    rejectCall,
    isConnected,
    notifications,
    unreadNotifications,
    markNotificationsSeen,
    clearNotifications,
    activeRooms,
    dispatch,
  } = useApp();

  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showFriendRequests, setShowFriendRequests] = useState(false);
  const [roomId, setRoomId] = useState("");
  const [roomError, setRoomError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [friends, setFriends] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("mixnmeet_theme") || "dark"
  );

  // Filter out current user and separate online/offline
  const otherUsers = onlineUsers.filter((user) => user.id !== currentUser?.id);
  const isUserOnline = (user) =>
    user.isOnline !== undefined ? user.isOnline : !!user.socketId;
  const onlineCount = otherUsers.filter((user) => isUserOnline(user)).length;

  const matchesSearch = (user) =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase());

  // Filter users based on search
  const filteredUsers = otherUsers.filter(matchesSearch);

  // Sort: online users first, then offline by last seen
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aOnline = isUserOnline(a);
    const bOnline = isUserOnline(b);
    if (aOnline && !bOnline) return -1;
    if (!aOnline && bOnline) return 1;
    if (!aOnline && !bOnline && a.lastSeen && b.lastSeen) {
      return new Date(b.lastSeen) - new Date(a.lastSeen);
    }
    return 0;
  });

  const friendsById = useMemo(() => {
    const map = new Map();
    friends.forEach((friend) => {
      map.set(friend._id, friend);
    });
    return map;
  }, [friends]);

  const onlineById = useMemo(() => {
    const map = new Map();
    onlineUsers.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [onlineUsers]);

  const friendsWithStatus = useMemo(() => {
    return friends.map((friend) => {
      const onlineInfo = onlineById.get(friend._id);
      return {
        ...friend,
        isOnline: onlineInfo?.isOnline,
        socketId: onlineInfo?.socketId,
        lastSeen: onlineInfo?.lastSeen,
      };
    });
  }, [friends, onlineById]);

  const sentRequestIds = useMemo(() => {
    return new Set(
      sentRequests.map((request) => request.receiverId?._id).filter(Boolean)
    );
  }, [sentRequests]);

  const peopleList = sortedUsers.filter((user) => !friendsById.has(user.id));
  const filteredFriends = friendsWithStatus.filter(matchesSearch);

  const handleVideoCall = (user) => {
    console.log("📹 Starting video call with:", user);
    callUser(user);
  };

  const fetchFriends = async () => {
    try {
      setFriendsLoading(true);
      const token = localStorage.getItem("mixnmeet_token");
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/friends/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setFriends(data.data);
      }
    } catch (error) {
      console.error("Error fetching friends:", error);
    } finally {
      setFriendsLoading(false);
    }
  };

  const fetchSentRequests = async () => {
    try {
      const token = localStorage.getItem("mixnmeet_token");
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/friends/requests/sent?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setSentRequests(data.data);
      }
    } catch (error) {
      console.error("Error fetching sent requests:", error);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem("mixnmeet_token");
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/friends/requests/pending?limit=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setPendingCount(data.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching pending count:", error);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      const token = localStorage.getItem("mixnmeet_token");
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/friends/request/${userId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchSentRequests();
      }
    } catch (error) {
      console.error("Error sending request:", error);
    }
  };

  useEffect(() => {
    fetchFriends();
    fetchSentRequests();
    fetchPendingCount();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") {
      root.classList.add("theme-light");
    } else {
      root.classList.remove("theme-light");
    }
    localStorage.setItem("mixnmeet_theme", theme);
  }, [theme]);

  useEffect(() => {
    if (notifications.some((item) => item.type === "friend-accepted")) {
      fetchFriends();
    }
    if (notifications.some((item) => item.type === "friend-request")) {
      fetchSentRequests();
      fetchPendingCount();
    }
  }, [notifications]);

  // Ensure a valid room ID before joining.
  const handleJoinRoom = () => {
    if (!roomId.trim()) {
      setRoomError("Enter a room ID to join");
      return;
    }
    setRoomError("");
    dispatch({
      type: "SET_ACTIVE_CALL",
      payload: {
        id: roomId,
        isVideo: true,
        isRoom: true,
        roomId: roomId.trim(),
      },
    });
  };

  const handleStartRoomCall = (room) => {
    dispatch({
      type: "SET_ACTIVE_CALL",
      payload: {
        id: room.id,
        isVideo: room.isVideo,
        isRoom: true,
        roomId: room.id,
        roomName: room.name,
      },
    });
  };

  const quickActions = [
    {
      icon: Video,
      title: "Start Room",
      description: "Create a new video call",
      color: "from-sky-500 to-blue-600",
      action: () => setShowCreateRoom(true),
    },
    {
      icon: UserPlus,
      title: "Join Room",
      description: "Enter room ID to join",
      color: "from-cyan-500 to-cyan-500",
      action: () => document.getElementById("room-input")?.focus(),
    },
    {
      icon: Calendar,
      title: "Schedule",
      description: "Plan future rooms",
      color: "from-green-500 to-green-600",
      action: () => setShowProfile(true),
    },
  ];

  // Show profile page if active
  if (showProfile) {
    return <ProfilePage onBack={() => setShowProfile(false)} />;
  }

  // Show friends page if active
  if (showFriends) {
    return <FriendsPage onBack={() => setShowFriends(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-emerald-950/40 to-slate-950">
      {/* Incoming Call Modal */}
      <AnimatePresence>
        {incomingCall && (
          <IncomingCallModal
            caller={incomingCall.callerInfo?.username || "Unknown"}
            onAccept={acceptCall}
            onReject={rejectCall}
          />
        )}
      </AnimatePresence>

      {/* Incoming Invite Modal */}
      <AnimatePresence>
        {incomingInvite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 p-8 rounded-2xl border border-slate-700 shadow-2xl max-w-sm w-full text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Room Invitation
              </h3>
              <p className="text-gray-300 mb-6">
                <span className="font-bold text-white">
                  {incomingInvite.inviterName}
                </span>{" "}
                invited you to join a room.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() =>
                    dispatch({ type: "SET_INCOMING_INVITE", payload: null })
                  }
                  className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-xl transition-colors"
                >
                  Decline
                </button>
                <button
                  onClick={() => {
                    dispatch({
                      type: "SET_ACTIVE_CALL",
                      payload: {
                        id: incomingInvite.roomId,
                        isVideo: true,
                        isRoom: true,
                        roomId: incomingInvite.roomId,
                      },
                    });
                    dispatch({ type: "SET_INCOMING_INVITE", payload: null });
                  }}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-500 text-white rounded-xl hover:shadow-lg transition-all"
                >
                  Join Room
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex h-screen relative">
        {/* Sidebar */}
        {isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/60 z-40"
            aria-label="Close sidebar"
          />
        )}
        <motion.div
          initial={false}
          animate={{ x: isSidebarOpen ? 0 : -320 }}
          className="fixed z-50 w-72 md:w-80 max-w-[85vw] bg-slate-800/70 backdrop-blur-xl border-r border-slate-700/50 flex flex-col h-full shadow-2xl"
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2"
              >
                <div className="w-9 h-9 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">MixNMeet</span>
              </motion.div>

              <div className="flex items-center gap-2 relative">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/60"
                  title="Toggle theme"
                >
                  {theme === "dark" ? (
                    <Sun className="w-4 h-4" />
                  ) : (
                    <Moon className="w-4 h-4" />
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    const next = !showNotifications;
                    setShowNotifications(next);
                    if (next) markNotificationsSeen();
                  }}
                  className="p-2 text-gray-400 hover:text-white transition-colors relative rounded-lg hover:bg-slate-700/60"
                  title="Notifications"
                >
                  <Bell className="w-4 h-4" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center font-semibold">
                      {unreadNotifications}
                    </span>
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/60"
                  title="Close sidebar"
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-4 z-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-white">
                        Notifications
                      </h4>
                      {notifications.length > 0 && (
                        <button
                          onClick={clearNotifications}
                          className="text-xs text-sky-300 hover:text-sky-200"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="text-sm text-gray-400">
                        No new notifications
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {notifications.map((item) => (
                          <div
                            key={item.id}
                            className="bg-slate-700/40 border border-slate-600/40 rounded-lg p-3 text-sm text-white"
                          >
                            <p>{item.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(item.createdAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* User Profile */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center space-x-3 cursor-pointer hover:bg-slate-700/30 p-3 rounded-xl transition-all"
              onClick={() => setShowProfile(true)}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {currentUser?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">
                  {currentUser?.username}
                </p>
                <div className="flex items-center text-sm">
                  <div
                    className={`w-2 h-2 rounded-full mr-2 ${
                      isConnected
                        ? "bg-green-500"
                        : "bg-yellow-500 animate-pulse"
                    }`}
                  ></div>
                  <span
                    className={
                      isConnected ? "text-green-400" : "text-yellow-400"
                    }
                  >
                    {isConnected ? "Online" : "Connecting..."}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </motion.div>
          </div>

          {/* Search */}
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>
          </div>

          {/* Friends & People */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                Friends
              </h3>
              <span className="text-xs text-gray-500">{friends.length}</span>
            </div>

            <div className="space-y-2">
              <AnimatePresence>
                {friendsLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-6"
                  >
                    <div className="animate-spin w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full mx-auto" />
                  </motion.div>
                ) : filteredFriends.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-6"
                  >
                    <p className="text-gray-400 text-sm">
                      {searchQuery ? "No friends found" : "No friends yet"}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {searchQuery
                        ? "Try a different search"
                        : "Send a friend request below."}
                    </p>
                  </motion.div>
                ) : (
                  filteredFriends.map((user, index) => (
                    <motion.div
                      key={user._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <UserCard
                        user={{ ...user, id: user._id }}
                        onAudioCall={() => handleVideoCall(user)}
                        onVideoCall={() => handleVideoCall(user)}
                      />
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-between mt-6 mb-3">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                People ({onlineCount} online)
              </h3>
            </div>

            <div className="space-y-2">
              <AnimatePresence>
                {peopleList.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-6"
                  >
                    <div className="w-12 h-12 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-gray-500" />
                    </div>
                    <p className="text-gray-400 text-sm">
                      {searchQuery ? "No users found" : "No other users yet"}
                    </p>
                  </motion.div>
                ) : (
                  peopleList.map((user, index) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <UserCard
                        user={user}
                        onAudioCall={() => handleVideoCall(user)}
                        onVideoCall={() => handleVideoCall(user)}
                        actionLabel={
                          sentRequestIds.has(user.id)
                            ? "Requested"
                            : "Add Friend"
                        }
                        actionDisabled={sentRequestIds.has(user.id)}
                        onAction={() => handleSendFriendRequest(user.id)}
                      />
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="mt-auto border-t border-slate-700/50 p-4 space-y-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFriends(true)}
              className="w-full flex items-center space-x-3 p-3 bg-slate-700/30 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <Heart className="w-5 h-5 text-emerald-400" />
              <span className="text-white font-medium">Friends</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowFriendRequests(true)}
              className="w-full flex items-center justify-between p-3 bg-slate-700/30 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-3">
                <UserPlus className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-medium">Friend Requests</span>
              </div>
              {pendingCount > 0 && (
                <span className="w-6 h-6 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center font-semibold">
                  {pendingCount}
                </span>
              )}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={leaveApp}
              className="w-full flex items-center space-x-3 p-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Logout</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Minified Sidebar (Desktop only when closed) */}
        {!isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="hidden md:flex fixed left-0 top-0 w-16 h-full bg-slate-800/70 backdrop-blur-xl border-r border-slate-700/50 flex-col items-center py-4 z-40 shadow-xl"
          >
            {/* Logo */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsSidebarOpen(true)}
              className="w-10 h-10 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl flex items-center justify-center mb-6"
              title="Open sidebar"
            >
              <Video className="w-5 h-5 text-white" />
            </motion.button>

            {/* Active Rooms - Quick Join */}
            {activeRooms.length > 0 && (
              <div className="w-full mb-4 flex flex-col items-center">
                <div className="text-center mb-2">
                  <span className="text-xs text-gray-400">
                    {activeRooms.length}
                  </span>
                </div>
                <div className="space-y-2 flex flex-col items-center">
                  {activeRooms.slice(0, 3).map((room) => (
                    <motion.button
                      key={room.id}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleStartRoomCall(room)}
                      className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-lg flex items-center justify-center group relative"
                      title={`Join ${room.name || room.id}`}
                    >
                      <Video className="w-4 h-4 text-emerald-400 group-hover:text-emerald-300" />
                      {room.participantCount > 0 && (
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-sky-500 rounded-full text-[8px] text-white flex items-center justify-center">
                          {room.participantCount}
                        </span>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {/* People Avatars */}
            <div className="flex-1 overflow-y-auto w-full space-y-2 flex flex-col items-center">
              {/* Friends */}
              {filteredFriends.slice(0, 5).map((user) => {
                const isOnline = isUserOnline(user);
                return (
                  <motion.button
                    key={user._id}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleVideoCall(user)}
                    className="relative w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center group"
                    title={user.username}
                  >
                    <span className="text-white font-semibold text-sm">
                      {user.username?.charAt(0).toUpperCase()}
                    </span>
                    {isOnline && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-800 rounded-full" />
                    )}
                  </motion.button>
                );
              })}

              {/* Other People */}
              {peopleList.slice(0, 5).map((user) => {
                const isOnline = isUserOnline(user);
                return (
                  <motion.button
                    key={user.id}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleVideoCall(user)}
                    className="relative w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center group"
                    title={user.username}
                  >
                    <span className="text-white font-semibold text-sm">
                      {user.username?.charAt(0).toUpperCase()}
                    </span>
                    {isOnline && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-800 rounded-full" />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Expand Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsSidebarOpen(true)}
              className="w-10 h-10 rounded-lg bg-slate-700/50 hover:bg-slate-700 flex items-center justify-center mt-4"
              title="Expand sidebar"
            >
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </motion.button>
          </motion.div>
        )}

        {/* Main Content */}
        <div
          className={`flex-1 flex flex-col ${
            isSidebarOpen ? "ml-0 md:ml-80" : "ml-0 md:ml-16"
          }`}
        >
          {/* Main Header */}
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-slate-800/30 backdrop-blur-xl border-b border-slate-700/50 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                    title="Open sidebar"
                  >
                    <span className="block w-5 h-0.5 bg-gray-300 mb-1" />
                    <span className="block w-5 h-0.5 bg-gray-300 mb-1" />
                    <span className="block w-5 h-0.5 bg-gray-300" />
                  </button>
                  <h1 className="text-3xl font-bold text-white">
                    Welcome back, {currentUser?.username}
                  </h1>
                </div>
                <p className="text-gray-400">
                  Ready to connect with your team?
                </p>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-gray-400">
                    {new Date().toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-lg font-semibold text-white">
                    {new Date().toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Content Area */}
          <div className="flex-1 p-8 overflow-y-auto">
            <div className="max-w-6xl mx-auto">
              {/* Quick Actions */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mb-12"
              >
                <h2 className="text-2xl font-bold text-white mb-6">
                  Quick Actions
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {quickActions.map((action, index) => (
                    <motion.div
                      key={action.title}
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="group cursor-pointer"
                      onClick={action.action}
                    >
                      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-700/50 transition-all duration-300">
                        <div
                          className={`w-12 h-12 bg-gradient-to-r ${action.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                        >
                          <action.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {action.title}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {action.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Live Rooms */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="mb-12"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-white">Live Rooms</h2>
                  <span className="text-sm text-gray-400">
                    {activeRooms.length} live
                  </span>
                </div>
                {activeRooms.length === 0 ? (
                  <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-gray-400">
                    No live rooms right now. Start one to invite others.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeRooms.map((room) => (
                      <div
                        key={room.id}
                        className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-5 flex items-center justify-between"
                      >
                        <div>
                          <p className="text-white font-semibold">
                            Room {room.id.slice(0, 8)}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Host: {room.host?.username || "Unknown"} ·{" "}
                            {room.participantsCount + 1} in call
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            dispatch({
                              type: "SET_ACTIVE_CALL",
                              payload: {
                                id: room.id,
                                isVideo: true,
                                isRoom: true,
                                roomId: room.id,
                              },
                            })
                          }
                          className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-zinc-950 rounded-xl hover:shadow-lg transition-all"
                        >
                          Join
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Join Room Section */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-12"
              >
                <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Join Existing Room
                  </h3>
                  <div className="flex flex-col md:flex-row md:space-x-4 space-y-3 md:space-y-0">
                    <input
                      id="room-input"
                      type="text"
                      value={roomId}
                      onChange={(e) => {
                        setRoomId(e.target.value);
                        if (roomError) setRoomError("");
                      }}
                      placeholder="Enter Room ID"
                      className={`flex-1 px-4 py-3 bg-slate-700/50 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        roomError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-slate-600/50 focus:border-emerald-500 focus:ring-emerald-500/20"
                      }`}
                      onKeyPress={(e) => e.key === "Enter" && handleJoinRoom()}
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleJoinRoom}
                      disabled={!roomId.trim()}
                      className="px-8 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-zinc-950 rounded-xl hover:shadow-lg hover:shadow-emerald-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Join
                    </motion.button>
                  </div>
                  {roomError && (
                    <p className="mt-3 text-xs text-red-300">{roomError}</p>
                  )}
                </div>
              </motion.div>

              {/* Stats Grid */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-4 gap-6"
              >
                {[
                  {
                    label: "Total Users",
                    value: otherUsers.length,
                    icon: Users,
                    color: "text-emerald-400",
                  },
                  {
                    label: "Online Now",
                    value: onlineCount,
                    icon: Activity,
                    color: "text-green-400",
                  },
                  {
                    label: "Rooms Today",
                    value: 0,
                    icon: Calendar,
                    color: "text-cyan-400",
                  },
                  {
                    label: "Total Time",
                    value: "0h",
                    icon: Clock,
                    color: "text-orange-400",
                  },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center"
                  >
                    <stat.icon
                      className={`w-8 h-8 ${stat.color} mx-auto mb-3`}
                    />
                    <div className={`text-3xl font-bold ${stat.color} mb-2`}>
                      {stat.value}
                    </div>
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Room Modal */}
      <AnimatePresence>
        {showCreateRoom && (
          <CreateRoomModal
            onClose={() => setShowCreateRoom(false)}
            onStartCall={handleStartRoomCall}
          />
        )}
      </AnimatePresence>

      {/* Friend Requests Modal */}
      <AnimatePresence>
        {showFriendRequests && (
          <FriendRequestsModal
            isOpen={showFriendRequests}
            onClose={() => setShowFriendRequests(false)}
            onAccept={() => {
              setShowFriendRequests(false);
            }}
            onReject={() => {
              setShowFriendRequests(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
